<script setup lang="ts">
import NProgress from 'nprogress'

import { object, string, ZodIssueCode } from 'zod'
import { EmployerInviteType } from '@swipe4work/api-client-fetch'
import { useForm } from '@/modules/form'
import { FirebaseAuthService } from '@/services/auth/firebase-auth-service'
import { AuthProvider } from '@/services/auth/auth-service'
import { AuthState } from '@/stores/auth-store'
import { captureException } from '@sentry/vue'

definePage({
  path: 'register',
  name: '/onboarding/register',
  meta: {
    layout: 'false',
    allowAnonymous: true,
  },
})

const authStore = useAuthStore()

const onboardingStore = useOnboardingStore()
const { invite, inviteId, inviteType } = storeToRefs(onboardingStore)

const registerModel = reactive({
  employerFirstName: invite.value?.firstName,
  employerLastName: invite.value?.lastName,
  email: invite.value?.email,
  password: '',
  confirmPassword: '',
})

const registerScheme = object({
  employerFirstName: string().min(1),
  employerLastName: string().min(1),
  password: string()
    .max(128, 'Wachtwoord mag maximaal 128 karakters zijn')
    .min(8, 'Wachtwoord moet minimaal 8 karakters zijn'),
  confirmPassword: string()
    .max(128, 'Wachtwoord mag maximaal 128 karakters zijn')
    .min(8, 'Wachtwoord moet minimaal 8 karakters zijn'),
}).superRefine((val, ctx) => {
  if (val.password !== val.confirmPassword) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: [ 'confirmPassword' ],
      message: 'Wachtwoorden komen niet overeen',
    })
  }
})

const form = useForm(registerModel, registerScheme)

async function register() {
  const entity = form.validate()
  if (!entity) {
    return
  }

  NProgress.start()
  const token = await onboardingStore.createEmployer(
    {
      firstName: entity.employerFirstName,
      lastName: entity.employerLastName,
      password: entity.password,
      inviteId: inviteId.value,
    },
  )
  if (token) {
    try {
      const firebaseAuthService = await authStore.getAuthService<FirebaseAuthService>(AuthProvider.Firebase, FirebaseAuthService, authStore.firebaseAuth)

      await firebaseAuthService.loginWithToken(token)
    } catch (error) {
      captureException(error)
      authStore.setAuthState(AuthState.AuthenticationError)
    }
    await authStore.fetchEmployerAccess()
    if (invite.value?.type === EmployerInviteType.Teammate) {
      await onboardingStore.finishOnboarding()
    } else {
      onboardingStore.goToPage(1)
    }
  }
  NProgress.done()
}
</script>

<template>
  <Message v-if="inviteType === EmployerInviteType.Referral" severity="success" :closable="false" class="mx-auto mb-4">
    {{ $t("onboarding.register.referralMessage", [invite?.invitedBy, invite?.invitedByCompany]) }}
  </Message>
  <Message v-if="inviteType === EmployerInviteType.Teammate" severity="success" :closable="false" class="mx-auto mb-4">
    {{ $t("onboarding.register.teammateMessage", [invite?.invitedBy, invite?.invitedByCompany]) }}
  </Message>
  <main>
    <form @submit.prevent="register" name="registration" data-form-type="register">
      <Card>
        <template #title>
          <h2>
            {{ $t("onboarding.register.welcome") }}
          </h2>
        </template>
        <template #content>
          <div class="min-h-[30rem]">
            <div>{{ $t("onboarding.register.welcomeDescription") }}</div>

            <div class="mt-4">
              <label class="text-sm" for="email"> {{ $t("onboarding.register.email") }} </label>
              <InputText
                id="email"
                v-model.trim="registerModel.email"
                class="w-100%"
                :readonly="true"
                autocomplete="email"
                name="email"
                type="email"
              />
            </div>
            <div class="mt-4">
              <label for="employerFirstName"> {{ $t("settings.firstName") }}* </label>
              <InputText
                id="employerFirstName"
                v-model.trim="registerModel.employerFirstName"
                class="w-100%"
                required="true"
                autocomplete="given-name"
                :class="{ 'p-invalid': form.hasError('employerFirstName') }"
              />
            </div>
            <div class="mt-4">
              <label for="employerLastName"> {{ $t("settings.lastName") }}* </label>
              <InputText
                id="employerLastName"
                v-model.trim="registerModel.employerLastName"
                class="w-100%"
                required="true"
                autocomplete="family-name"
                :class="{ 'p-invalid': form.hasError('employerLastName') }"
              />
            </div>

            <div class="mt-4">
              <label class="block text-sm" for="password"> {{ $t("password") }} </label>
              <Password
                id="password"
                v-model="registerModel.password"
                toggle-mask
                :required="true"
                :feedback="false"
                :input-props="{
                  autocomplete: 'new-password',
                  name: 'password',
                }"
                :class="{ 'p-invalid': form.hasError('password') }"
              />
              <small v-if="form.hasError('password')" class="block color-red-700">
                {{ form.errorMessage('password') }}
              </small>
            </div>
            <div class="mt-4">
              <label class="block text-sm" for="confirmPassword"> {{ $t("confirmPassword") }} </label>
              <Password
                id="confirmPassword"
                v-model="registerModel.confirmPassword"
                toggle-mask
                :required="true"
                :feedback="false"
                :input-props="{
                  autocomplete: 'new-password',
                  name: 'confirmPassword',
                }"
                :class="{ 'p-invalid': form.hasError('confirmPassword') }"
              />
              <small v-if="form.hasError('confirmPassword')" class="block color-red-700">
                {{ form.errorMessage('confirmPassword') }}
              </small>
            </div>
          </div>
        </template>
        <template #footer>
          <div class="flex justify-end">
            <Button
              v-click-async="register"
              rounded
              type="submit"
              :label="$t('onboarding.register.submit')"
            />
          </div>
        </template>
      </Card>
    </form>
  </main>
</template>
